import { z } from 'zod';
import { IdSchema, DateTimeSchema, PaginationParamsSchema, HttpOrInternalFileUrlSchema, } from '../../common';
// ============================================
// Enums (wire-format — lowercase)
// ============================================
export const ExamStatusSchema = z.enum(['draft', 'published', 'closed']);
export const QuestionTypeSchema = z.enum([
    'multiple_choice',
    'true_false',
    'short_answer',
    'essay',
    'classic',
]);
export const AttemptStatusSchema = z.enum(['in_progress', 'submitted', 'graded']);
// ============================================
// Question payload — per type
// ============================================
const MultipleChoiceChoiceSchema = z
    .object({
    id: z.string().min(1).max(8),
    text: z.string().min(1).max(500),
})
    .strict();
export const MultipleChoicePayloadSchema = z
    .object({
    choices: z.array(MultipleChoiceChoiceSchema).min(2).max(8),
    correctChoiceId: z.string().min(1).max(8),
})
    .strict()
    .superRefine((payload, ctx) => {
    const ids = payload.choices.map((choice) => choice.id);
    if (new Set(ids).size !== ids.length) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['choices'],
            message: 'choice id değerleri benzersiz olmalı',
        });
    }
    if (!ids.includes(payload.correctChoiceId)) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['correctChoiceId'],
            message: 'correctChoiceId mevcut seçeneklerden biri olmalı',
        });
    }
});
// D3-7 — Çoklu-cümle Doğru/Yanlış: her cümlenin kendi D(true)/Y(false) işareti.
export const TrueFalseStatementSchema = z
    .object({
    text: z.string().min(1).max(1000),
    isTrue: z.boolean(),
})
    .strict();
export const TrueFalsePayloadSchema = z
    .object({
    // Legacy: tek doğru/yanlış değeri (geriye uyumluluk).
    correctValue: z.boolean().optional(),
    // Çoklu cümle: soru metni giriş cümlesi; her statement ayrı işaretlenir.
    // Hepsi doğru işaretlenirse soru doğru sayılır.
    statements: z.array(TrueFalseStatementSchema).min(1).max(20).optional(),
})
    .strict()
    .refine((d) => d.correctValue !== undefined || (d.statements?.length ?? 0) > 0, {
    message: 'true_false payload: correctValue veya statements gerekli',
});
export const ShortAnswerPayloadSchema = z
    .object({
    expectedAnswer: z.string().max(500).optional(),
    caseSensitive: z.boolean().default(false),
})
    .strict();
export const EssayPayloadSchema = z
    .object({
    rubric: z.string().max(2000).optional(),
    /** Klasik sorunun doğru cevabı (öğretmen anahtarı). */
    modelAnswer: z.string().max(4000).optional(),
})
    .strict();
export const QuestionPayloadSchema = z.union([
    MultipleChoicePayloadSchema,
    TrueFalsePayloadSchema,
    EssayPayloadSchema,
    ShortAnswerPayloadSchema,
]);
// ============================================
// Question — bağımsız Soru Havuzu kaydı
// ============================================
export const QuestionStatusSchema = z.enum(['draft', 'published', 'archived']);
const QuestionBaseSchema = z.object({
    id: IdSchema,
    text: z.string(),
    explanation: z.string().nullable(),
    imageUrl: z.string().nullable(),
    solutionVideoUrl: z.string().nullable(),
    subjectId: IdSchema.nullable(),
    unitId: IdSchema.nullable(),
    topicId: IdSchema.nullable(),
    outcomeId: IdSchema.nullable(),
    gradeLevel: z.number().int().min(1).max(12).nullable(),
    difficulty: z.number().int().min(1).max(5),
    status: QuestionStatusSchema,
    tags: z.array(z.string()).default([]),
    createdById: IdSchema.nullable(),
    createdAt: DateTimeSchema,
    updatedAt: DateTimeSchema,
    // Embedded summary
    subjectName: z.string().nullable().optional(),
    unitName: z.string().nullable().optional(),
    topicName: z.string().nullable().optional(),
    usageCount: z.number().int().optional(),
});
export const QuestionSchema = z.discriminatedUnion('type', [
    QuestionBaseSchema.extend({
        type: z.literal('multiple_choice'),
        payload: MultipleChoicePayloadSchema,
    }),
    QuestionBaseSchema.extend({ type: z.literal('true_false'), payload: TrueFalsePayloadSchema }),
    QuestionBaseSchema.extend({ type: z.literal('short_answer'), payload: ShortAnswerPayloadSchema }),
    QuestionBaseSchema.extend({ type: z.literal('essay'), payload: EssayPayloadSchema }),
    QuestionBaseSchema.extend({ type: z.literal('classic'), payload: EssayPayloadSchema }),
]);
// Görsel hem `https://...` hem data URL kabul eder (cihazdan yüklenen
// soru görselleri bulk akışta data:image/png;base64,... olarak gelir).
const ImageOrDataUrl = z
    .string()
    .min(1)
    .max(5_000_000)
    .refine((v) => v.startsWith('data:image/') || HttpOrInternalFileUrlSchema.safeParse(v).success, 'imageUrl must be a https URL, internal file path or data:image/* URL');
const CreateQuestionBaseSchema = z.object({
    text: z.string().min(1).max(4000),
    explanation: z.string().max(4000).optional(),
    imageUrl: ImageOrDataUrl.optional(),
    solutionVideoUrl: HttpOrInternalFileUrlSchema.optional(),
    subjectId: IdSchema.optional(),
    unitId: IdSchema.optional(),
    topicId: IdSchema.optional(),
    outcomeId: IdSchema.optional(),
    gradeLevel: z.coerce.number().int().min(1).max(12).optional(),
    difficulty: z.coerce.number().int().min(1).max(5).optional(),
    status: QuestionStatusSchema.optional(),
    tags: z.array(z.string().min(1).max(40)).max(20).optional(),
});
export const CreateQuestionSchema = z.discriminatedUnion('type', [
    CreateQuestionBaseSchema.extend({
        type: z.literal('multiple_choice'),
        payload: MultipleChoicePayloadSchema,
    }),
    CreateQuestionBaseSchema.extend({
        type: z.literal('true_false'),
        payload: TrueFalsePayloadSchema,
    }),
    CreateQuestionBaseSchema.extend({
        type: z.literal('short_answer'),
        payload: ShortAnswerPayloadSchema,
    }),
    CreateQuestionBaseSchema.extend({ type: z.literal('essay'), payload: EssayPayloadSchema }),
    CreateQuestionBaseSchema.extend({ type: z.literal('classic'), payload: EssayPayloadSchema }),
]);
export const UpdateQuestionSchema = CreateQuestionBaseSchema.partial()
    .extend({
    type: QuestionTypeSchema.optional(),
    payload: QuestionPayloadSchema.optional(),
    explanation: z.string().max(4000).nullable().optional(),
    imageUrl: ImageOrDataUrl.nullable().optional(),
    solutionVideoUrl: HttpOrInternalFileUrlSchema.nullable().optional(),
    subjectId: IdSchema.nullable().optional(),
    unitId: IdSchema.nullable().optional(),
    topicId: IdSchema.nullable().optional(),
    outcomeId: IdSchema.nullable().optional(),
    gradeLevel: z.number().int().min(1).max(12).nullable().optional(),
})
    .superRefine((value, ctx) => {
    const hasType = value.type !== undefined;
    const hasPayload = value.payload !== undefined;
    if (hasType !== hasPayload) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: hasType ? ['payload'] : ['type'],
            message: 'Soru tipi ve payload birlikte güncellenmeli',
        });
        return;
    }
    if (!value.type || !value.payload)
        return;
    const schemaByType = {
        multiple_choice: MultipleChoicePayloadSchema,
        true_false: TrueFalsePayloadSchema,
        short_answer: ShortAnswerPayloadSchema,
        essay: EssayPayloadSchema,
        classic: EssayPayloadSchema,
    };
    if (!schemaByType[value.type].safeParse(value.payload).success) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['payload'],
            message: `payload, ${value.type} soru tipiyle uyumlu değil`,
        });
    }
});
export const QuestionQuerySchema = PaginationParamsSchema.extend({
    // Soru havuzu PDF'ten 200+ sorulu yuklemeye gore tasarli — sinav olusturma
    // dialogunda kullanici "Sayfa basina 200" gorebilmeli, bu yuzden globaldeki
    // 100'luk limit override edilir.
    limit: z.coerce.number().int().positive().max(500).default(20),
    search: z.string().optional(),
    subjectId: IdSchema.optional(),
    unitId: IdSchema.optional(),
    topicId: IdSchema.optional(),
    outcomeId: IdSchema.optional(),
    gradeLevel: z.coerce.number().int().min(1).max(12).optional(),
    type: QuestionTypeSchema.optional(),
    status: QuestionStatusSchema.optional(),
    difficulty: z.coerce.number().int().min(1).max(5).optional(),
    tag: z.string().optional(),
    /** true → daha önce hiçbir sınavda kullanılmamış sorular */
    unusedOnly: z.coerce.boolean().optional(),
});
// Bulk create — toplu soru ekleme (PDF #7 "Toplu Soru Ekleme")
export const BulkCreateQuestionsSchema = z.object({
    defaults: z
        .object({
        subjectId: IdSchema.optional(),
        unitId: IdSchema.optional(),
        topicId: IdSchema.optional(),
        gradeLevel: z.coerce.number().int().min(1).max(12).optional(),
        type: QuestionTypeSchema.optional(),
        difficulty: z.coerce.number().int().min(1).max(5).optional(),
        status: QuestionStatusSchema.optional(),
        tags: z.array(z.string().min(1).max(40)).max(20).optional(),
    })
        .optional(),
    numbering: z
        .object({
        subjectId: IdSchema,
        unitId: IdSchema,
        topicId: IdSchema,
        gradeLevel: z.number().int().min(1).max(12),
        titlePrefix: z.string().min(1).max(500),
        separator: z.enum([' ', ' - ']),
        autoItemIndexes: z.array(z.number().int().min(0).max(99)).max(100),
    })
        .optional(),
    items: z.array(CreateQuestionSchema).min(1).max(100),
});
// Bulk delete — toplu soru silme
export const BulkDeleteQuestionsSchema = z.object({
    ids: z.array(IdSchema).min(1).max(500),
});
// Sınava soru ekleme — pivot
export const AddQuestionsToExamSchema = z.object({
    questionIds: z.array(IdSchema).min(1).max(200),
});
export const ExamQuestionSchema = z.object({
    id: IdSchema,
    examId: IdSchema,
    questionId: IdSchema,
    orderIndex: z.number().int(),
    points: z.number().int().min(1).max(100),
    createdAt: DateTimeSchema,
    // hydrated
    question: QuestionSchema.optional(),
});
export const UpdateExamQuestionSchema = z.object({
    orderIndex: z.coerce.number().int().nonnegative().optional(),
    points: z.coerce.number().int().min(1).max(100).optional(),
});
// ============================================
// Exam Assignment — PDF #8.3 (Course/Camp/Private)
// ============================================
export const ExamAssignmentTargetSchema = z.enum(['course', 'camp', 'student']);
export const ExamAssignmentSchema = z.object({
    id: IdSchema,
    examId: IdSchema,
    target: ExamAssignmentTargetSchema,
    targetId: IdSchema,
    assignedById: IdSchema.nullable(),
    createdAt: DateTimeSchema,
    // Embedded summary — UI display
    targetName: z.string().nullable().optional(),
});
export const CreateExamAssignmentSchema = z.object({
    target: ExamAssignmentTargetSchema,
    targetIds: z.array(IdSchema).min(1).max(200),
});
// ============================================
// Exam
// ============================================
export const ExamSchema = z.object({
    id: IdSchema,
    courseId: IdSchema.nullable(),
    title: z.string(),
    description: z.string().nullable(),
    status: ExamStatusSchema,
    subjectId: IdSchema.nullable(),
    topicId: IdSchema.nullable(),
    durationMin: z.number().int().nullable(),
    passingScore: z.number().int().min(0).max(100).nullable(),
    shuffleQuestions: z.boolean(),
    shuffleAnswers: z.boolean(),
    showAnswersAfter: z.boolean(),
    maxAttempts: z.number().int().min(1).max(10),
    imageUrl: z.string().nullable(),
    category: z.string().nullable(),
    startsAt: DateTimeSchema.nullable(),
    endsAt: DateTimeSchema.nullable(),
    allowLateSubmission: z.boolean().default(false),
    createdById: IdSchema.nullable(),
    createdAt: DateTimeSchema,
    updatedAt: DateTimeSchema,
    // Embedded summary
    questionCount: z.number().int().optional(),
    attemptCount: z.number().int().optional(),
    courseName: z.string().nullable().optional(),
    subjectName: z.string().nullable().optional(),
    teacherName: z.string().nullable().optional(),
    gradeLevels: z.array(z.number().int()).optional(),
    unitNames: z.array(z.string()).optional(),
    submittedCount: z.number().int().nonnegative().optional(),
    totalCount: z.number().int().nonnegative().optional(),
});
const CreateExamObjectSchema = z.object({
    title: z.string().min(1).max(200),
    description: z.string().max(4000).optional(),
    courseId: IdSchema.optional(),
    subjectId: IdSchema.optional(),
    topicId: IdSchema.optional(),
    durationMin: z.coerce.number().int().min(1).max(600).optional(),
    passingScore: z.coerce.number().int().min(0).max(100).optional(),
    shuffleQuestions: z.boolean().optional(),
    shuffleAnswers: z.boolean().optional(),
    showAnswersAfter: z.boolean().optional(),
    maxAttempts: z.coerce.number().int().min(1).max(10).optional(),
    imageUrl: z.string().max(2_000_000).optional(),
    category: z.string().max(80).optional(),
    startsAt: z.string().datetime().nullable().optional(),
    endsAt: z.string().datetime().nullable().optional(),
    allowLateSubmission: z.boolean().optional(),
});
const validExamDates = (data) => !data.startsAt || !data.endsAt || new Date(data.startsAt) <= new Date(data.endsAt);
export const CreateExamSchema = CreateExamObjectSchema.refine(validExamDates, {
    path: ['endsAt'],
    message: 'Son tarih başlangıç tarihinden önce olamaz',
});
export const UpdateExamSchema = CreateExamObjectSchema.partial()
    .extend({
    status: ExamStatusSchema.optional(),
    startsAt: z.string().datetime().nullable().optional(),
    endsAt: z.string().datetime().nullable().optional(),
})
    .refine(validExamDates, {
    path: ['endsAt'],
    message: 'Son tarih başlangıç tarihinden önce olamaz',
});
export const ExamQuerySchema = PaginationParamsSchema.extend({
    search: z.string().optional(),
    status: ExamStatusSchema.optional(),
    courseId: IdSchema.optional(),
    subjectId: IdSchema.optional(),
    gradeLevel: z.coerce.number().int().min(1).max(12).optional(),
    teacherId: IdSchema.optional(),
    unitId: IdSchema.optional(),
});
// ============================================
// Attempt + Answer
// ============================================
export const AnswerResponseSchema = z.union([
    z.object({ choiceId: z.string().min(1).max(8) }),
    z.object({ value: z.boolean() }),
    // D3-7 — çoklu-cümle Doğru/Yanlış: cümle sırasına göre işaretlenen D/Y dizisi.
    z.object({ values: z.array(z.boolean()).min(1).max(20) }),
    z.object({ text: z.string().max(10_000) }),
]);
export const AnswerSchema = z.object({
    id: IdSchema,
    attemptId: IdSchema,
    questionId: IdSchema,
    response: AnswerResponseSchema,
    isCorrect: z.boolean().nullable(),
    earnedPoints: z.number().int().nullable(),
    feedback: z.string().nullable(),
    createdAt: DateTimeSchema,
    updatedAt: DateTimeSchema,
});
export const ExamAttemptSchema = z.object({
    id: IdSchema,
    examId: IdSchema,
    studentUserId: IdSchema,
    status: AttemptStatusSchema,
    attemptNo: z.number().int(),
    score: z.number().int().nullable(),
    totalPoints: z.number().int().nullable(),
    startedAt: DateTimeSchema,
    submittedAt: DateTimeSchema.nullable(),
    gradedAt: DateTimeSchema.nullable(),
    gradedById: IdSchema.nullable(),
    studentName: z.string().nullable().optional(),
});
export const SubmitAnswerSchema = z.object({
    questionId: IdSchema,
    response: AnswerResponseSchema,
});
export const SubmitAttemptSchema = z.object({
    answers: z.array(SubmitAnswerSchema).min(1),
});
export const GradeAnswerSchema = z.object({
    earnedPoints: z.coerce.number().int().min(0),
    feedback: z.string().max(2000).optional(),
});
//# sourceMappingURL=exam.js.map